import { hc } from "hono/client";
import type { ApiType } from "@cdc-sccr/api/src/routes/types";
import { getAuth } from "./auth";
import { emitUnauthorized } from "./unauthorized";
import { toApiError } from "./api-error";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001";
const API_KEY = import.meta.env.VITE_API_KEY ?? "dev-api-key-change-in-production";

export function getHeaders(): Record<string, string> {
  const headers: Record<string, string> = { "X-API-Key": API_KEY };
  const auth = getAuth();
  if (auth) {
    headers["Authorization"] = `Bearer ${auth.token}`;
    headers["X-CSRF-Token"] = auth.csrfToken;
  }
  return headers;
}

function handleResponse(response: Response): Response {
  // A failed login is also a 401, but it is not an expired session.
  if (response.status === 401 && getAuth()) {
    emitUnauthorized();
  }
  return response;
}

async function withHandledFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, init);
  const handled = handleResponse(response);
  if (!handled.ok) {
    throw await toApiError(handled);
  }
  return handled;
}

export const api = hc<ApiType>(`${API_URL}/api`, {
  headers: getHeaders,
  fetch: (input: RequestInfo | URL, init?: RequestInit) => withHandledFetch(input, init),
});

export function buildRequestInit(init?: RequestInit): RequestInit {
  const baseHeaders = getHeaders();
  let extraHeaders: Record<string, string> = {};

  if (init?.headers instanceof Headers) {
    extraHeaders = Object.fromEntries(init.headers.entries());
  } else if (Array.isArray(init?.headers)) {
    extraHeaders = Object.fromEntries(init.headers);
  } else if (init?.headers) {
    extraHeaders = init.headers as Record<string, string>;
  }

  return {
    ...init,
    headers: { ...baseHeaders, ...extraHeaders },
  };
}

export async function authorizedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  return withHandledFetch(input, buildRequestInit(init));
}
